(() => {
  window.theme = window.theme || {};
  if (window.theme.customMegaMenu?.initialize) {
    window.theme.customMegaMenu.initialize(document);
    return;
  }

  const desktopMedia = window.matchMedia('(min-width: 1068px)');
  const instances = new Map();

  class CustomMegaNavigation {
    constructor(root) {
      this.root = root;
      this.items = [...root.querySelectorAll(':scope > .thb-full-menu > .custom-mega-menu-item')];
      this.activeItem = null;
      this.closeTimer = null;
      this.hideTimers = new WeakMap();
      this.abortController = null;
      this.onMediaChange = this.onMediaChange.bind(this);
      desktopMedia.addEventListener('change', this.onMediaChange);
      this.onMediaChange();
    }

    onMediaChange() {
      if (desktopMedia.matches) {
        this.activate();
      } else {
        this.deactivate();
      }
    }

    activate() {
      if (this.abortController || !this.items.length) return;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.items.forEach((item) => {
        const trigger = item.querySelector(':scope > .thb-full-menu--link');
        const panel = item.querySelector(':scope > [data-custom-mega-panel]');
        if (!trigger || !panel) return;

        trigger.addEventListener('mouseenter', () => this.open(item), { signal });
        item.addEventListener('mouseenter', () => this.clearCloseTimer(), { signal });
        item.addEventListener('mouseleave', () => this.scheduleClose(item), { signal });
        item.addEventListener('focusin', () => this.open(item), { signal });
        item.addEventListener('focusout', () => {
          requestAnimationFrame(() => {
            if (!item.contains(document.activeElement)) this.scheduleClose(item);
          });
        }, { signal });
      });

      document.addEventListener('pointerdown', (event) => {
        if (this.activeItem && !this.activeItem.contains(event.target)) this.close(this.activeItem, true);
      }, { signal });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.activeItem) return;
        const trigger = this.getTrigger(this.activeItem);
        this.close(this.activeItem, true);
        if (trigger) trigger.focus();
      }, { signal });
    }

    deactivate() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      if (this.activeItem) this.close(this.activeItem, true);
      this.items.forEach((item) => {
        const panel = this.getPanel(item);
        const trigger = this.getTrigger(item);
        if (panel) {
          panel.classList.remove('is-open');
          panel.hidden = true;
        }
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
    }

    destroy() {
      this.clearCloseTimer();
      this.items.forEach((item) => {
        const panel = this.getPanel(item);
        if (panel) clearTimeout(this.hideTimers.get(panel));
      });
      this.deactivate();
      desktopMedia.removeEventListener('change', this.onMediaChange);
      delete this.root.dataset.customMegaReady;
    }

    getTrigger(item) {
      return item.querySelector(':scope > .thb-full-menu--link');
    }

    getPanel(item) {
      return item.querySelector(':scope > [data-custom-mega-panel]');
    }

    open(item) {
      if (!desktopMedia.matches || item === this.activeItem) {
        this.clearCloseTimer();
        return;
      }

      if (this.activeItem) this.close(this.activeItem, true);
      const panel = this.getPanel(item);
      const trigger = this.getTrigger(item);
      if (!panel || !trigger) return;

      clearTimeout(this.hideTimers.get(panel));
      panel.hidden = false;
      requestAnimationFrame(() => panel.classList.add('is-open'));
      trigger.setAttribute('aria-expanded', 'true');
      this.activeItem = item;
    }

    scheduleClose(item) {
      this.clearCloseTimer();
      const panel = this.getPanel(item);
      const delay = Number(panel?.dataset.closeDelay || 250);
      this.closeTimer = window.setTimeout(() => this.close(item), delay);
    }

    clearCloseTimer() {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    close(item, immediate = false) {
      const panel = this.getPanel(item);
      const trigger = this.getTrigger(item);
      if (!panel) return;

      this.clearCloseTimer();
      panel.classList.remove('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (this.activeItem === item) this.activeItem = null;

      clearTimeout(this.hideTimers.get(panel));
      const wait = immediate || panel.classList.contains('custom-mega-menu--no-animation') ? 0 : 190;
      if (wait === 0) {
        panel.hidden = true;
        return;
      }
      const hideTimer = window.setTimeout(() => {
        if (!panel.classList.contains('is-open')) panel.hidden = true;
      }, wait);
      this.hideTimers.set(panel, hideTimer);
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('full-menu[data-custom-mega-navigation]').forEach((root) => {
      if (root.dataset.customMegaReady === 'true') return;
      root.dataset.customMegaReady = 'true';
      instances.set(root, new CustomMegaNavigation(root));
    });
  };

  const destroy = (scope) => {
    scope.querySelectorAll('full-menu[data-custom-mega-navigation]').forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  };

  window.theme.customMegaMenu = { initialize, destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target.closest('.custom-mega-menu-item');
    const root = item?.closest('full-menu[data-custom-mega-navigation]');
    const instance = root ? instances.get(root) : null;
    if (item && instance && desktopMedia.matches) instance.open(item);
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const item = event.target.closest('.custom-mega-menu-item');
    const root = item?.closest('full-menu[data-custom-mega-navigation]');
    const instance = root ? instances.get(root) : null;
    if (item && instance) instance.scheduleClose(item);
  });
})();
